import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { Moon, Sun, Monitor } from 'lucide-react';
import { COMMAND_PALETTE_NAVIGATION, formatShortcut } from './commandPaletteItems';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  const { t } = useTranslation('common');

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title={t('commandPalette.title')}>
      <CommandInput placeholder={t('commandPalette.placeholder')} />
      <CommandList>
        <CommandEmpty>{t('commandPalette.noResults')}</CommandEmpty>

        <CommandGroup heading={t('commandPalette.navigation')}>
          {COMMAND_PALETTE_NAVIGATION.map((item) => (
            <CommandItem
              key={item.path}
              onSelect={() => runCommand(() => navigate(item.path))}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{t(item.labelKey)}</span>
              {item.shortcut && <CommandShortcut>{formatShortcut(item.shortcut)}</CommandShortcut>}
            </CommandItem>
          ))}
        </CommandGroup>

        {/* Die Aktions-Gruppe ist ersatzlos weg (Task 13): ihre drei Eintraege
            sprangen auf `/` und warfen ein CustomEvent, auf das seit dem Umbau
            der Startseite niemand mehr hoert. Wer sie zurueckholt, braucht
            zuerst einen Empfaenger. */}

        <CommandSeparator />

        <CommandGroup heading={t('commandPalette.theme')}>
          <CommandItem onSelect={() => runCommand(() => setTheme('light'))}>
            <Sun className="mr-2 h-4 w-4" />
            <span>{t('theme.light')}</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => setTheme('dark'))}>
            <Moon className="mr-2 h-4 w-4" />
            <span>{t('theme.dark')}</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => setTheme('system'))}>
            <Monitor className="mr-2 h-4 w-4" />
            <span>{t('theme.system')}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
